import { DateField } from '~/common/components';
import { useI18n, Trans } from '~/common/context';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Card,
  CenteredSpinner,
  Col,
  FormContainer,
  Row,
  Title,
  notify,
} from '@wyz/ui';
import { DateUtils } from '@wyz/utils';
import { isNil } from 'lodash-es';
import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useManageQuotePricingPolicies } from '~/purchase/api';
import { usePricingPolicyForm } from '~/purchase/components';
import { QuotePricingPolicyHelpers } from '~/purchase/helpers';
import {
  PRICING_STATUS_ENUM,
  quotePricingApplicationStepFormSchema,
} from '~/purchase/schemas';
import { usePricingPolicyErrorTranslation } from '~/common/hooks';
import {
  IGlobalQuotePricingFormData,
  IPricingStatus,
  IQuotePricingApplicationStepForm,
} from '~/purchase/types';

type CommonFormProps = {
  globalFormData: IGlobalQuotePricingFormData;
  customerId: string;
  policyGroupId?: number;
};

type ApplicationStepFormProps = CommonFormProps & {
  onSubmit: (
    data: IQuotePricingApplicationStepForm,
    status: IPricingStatus,
  ) => void;
};

const ApplicationStepForm: React.FC<ApplicationStepFormProps> = ({
  onSubmit,
  globalFormData,
  customerId,
  policyGroupId,
}) => {
  const { t } = useI18n();
  const { resolveErrorKey } = usePricingPolicyErrorTranslation();
  const pricingPolicyForm = usePricingPolicyForm();
  const methods = useForm<IQuotePricingApplicationStepForm>({
    defaultValues: globalFormData.APPLICATION,
    resolver: zodResolver(quotePricingApplicationStepFormSchema),
    shouldUnregister: true,
  });
  const watchedStartDate = useWatch({
    control: methods.control,
    name: 'startDate',
  });
  const manageQuotePricingPolicies = useManageQuotePricingPolicies({
    checkOverlap: { enabled: true },
  });
  return (
    <FormContainer
      methods={methods}
      submitHandler={(formData, event) => {
        manageQuotePricingPolicies.checkOverlap.mutate(
          {
            customerIds: [customerId],
            policyGroupId,
            startDate: DateUtils.formatTimestamp(formData.startDate, {
              customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
            }),
            endDate: formData.endDate
              ? DateUtils.formatTimestamp(formData.endDate, {
                  customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
                })
              : null,
            default: false,
          },
          {
            onError: (error) => {
              notify.error(<Trans i18nKey={resolveErrorKey(error.message)} />, {
                id: 'toast-error-quote-pricing-policy-overlap-check',
              });
            },
            onSuccess: () => {
              onSubmit(
                formData,
                ((event?.nativeEvent as SubmitEvent)?.submitter?.getAttribute(
                  'data-status',
                ) as IPricingStatus) ?? PRICING_STATUS_ENUM.DRAFT,
              );
            },
          },
        );
      }}
      id={QuotePricingPolicyHelpers.QUOTE_PRICING_POLICY_STEP_INDEX.APPLICATION.toString()}
      formConfiguration={{ mode: pricingPolicyForm.mode }}
    >
      <Card>
        {manageQuotePricingPolicies.checkOverlap.isLoading && (
          <div
            className='position-absolute h-100 w-100'
            style={{ backgroundColor: 'rgba(0,0,0,.1)', zIndex: 5 }}
          >
            <div className='center-v-h'>
              <CenteredSpinner />
            </div>
          </div>
        )}
        <Card.Header>
          <Card.Title>
            <Title heading='xl' weight='bold'>
              <Trans
                i18nKey='app.quote.pricing_policy.form.step3.title'
                values={{
                  name: globalFormData.PRICING?.name,
                }}
              />
            </Title>
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col sm={12} md={6}>
              <DateField
                name='startDate'
                label={<Trans i18nKey='app.common.start_date' />}
                placeholderText='dd-MM-yyyy'
                dateFormat={DateUtils.DATE_FORMAT.DMY}
                required
                showTimeSelect={false}
                translations={{
                  applyButton: t('app.common.button.apply'),
                  resetButton: t('app.common.button.reset'),
                }}
              />
            </Col>
            {watchedStartDate && (
              <Col sm={12} md={6}>
                <DateField
                  name='endDate'
                  label={<Trans i18nKey='app.common.end_date' />}
                  dateFormat={DateUtils.DATE_FORMAT.DMY}
                  placeholderText='dd-MM-yyyy'
                  showTimeSelect={false}
                  minDate={
                    isNil(watchedStartDate) ? undefined : watchedStartDate
                  }
                  translations={{
                    applyButton: t('app.common.button.apply'),
                    resetButton: t('app.common.button.reset'),
                  }}
                />
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>
    </FormContainer>
  );
};

export default ApplicationStepForm;
